import React, { useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import SupportButton from '../components/SupportButton';
import EmailCaptureModal from '../components/EmailCaptureModal';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { 
  Upload,
  Loader2,
  Mail,
  CheckCircle,
  Download,
  ArrowRight,
  Shield,
  Zap,
  AlertCircle,
  Sparkles,
  AlertTriangle,
  XCircle,
  FileSpreadsheet
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EmailFormatterPage = () => {
  const [file, setFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const fileInputRef = useRef(null);
  const { user } = useAuth();

  const handleFileSelect = useCallback((selectedFile) => {
    if (!selectedFile) return;
    
    const validTypes = ['.csv', '.xlsx', '.xls'];
    const fileExt = selectedFile.name.toLowerCase().slice(selectedFile.name.lastIndexOf('.'));
    
    if (!validTypes.includes(fileExt)) {
      toast.error('Please upload a CSV or Excel file');
      return;
    }
    
    if (selectedFile.size > 50 * 1024 * 1024) {
      toast.error('File size must be under 50MB');
      return;
    }
    
    setFile(selectedFile);
    setResult(null);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    handleFileSelect(droppedFile);
  }, [handleFileSelect]);

  const processFile = async () => {
    if (!file) return;
    
    setProcessing(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API_URL}/api/tools/email-formatter/process`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000
        }
      );
      
      setResult(response.data);
      
      if (response.data.total_rows > 100) {
        toast.info(`Fixed first 100 rows. ${response.data.total_rows - 100} rows remaining - sign up to fix the rest.`);
      } else {
        toast.success('All emails processed successfully!');
      }
      
    } catch (error) {
      console.error('Processing error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to process file';
      toast.error(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  const resetTool = () => {
    setFile(null);
    setResult(null);
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'valid': return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      case 'fixed': return <Zap className="w-4 h-4 text-blue-500" />;
      case 'invalid': return <XCircle className="w-4 h-4 text-red-500" />;
      default: return <AlertCircle className="w-4 h-4 text-amber-500" />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Shared Header */}
      <Header />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-8 sm:pb-12">
        {/* Hero */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 rounded-full text-blue-700 text-sm font-medium mb-4">
            <Mail className="w-4 h-4" />
            Free Email Fixer
          </div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-4">
            Email Address Fixer
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Fix common email typos and validate email addresses. 
            Perfect for cleaning your HubSpot, Salesforce, or any CRM export.
          </p>
        </div>

        {/* Features */}
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
            <Zap className="w-6 h-6 text-blue-500 mx-auto mb-2" />
            <p className="font-medium text-slate-900">Fix Typos</p>
            <p className="text-sm text-slate-500">gmal.com → gmail.com</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
            <CheckCircle className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
            <p className="font-medium text-slate-900">First 100 Free</p>
            <p className="text-sm text-slate-500">No signup required</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
            <AlertTriangle className="w-6 h-6 text-amber-500 mx-auto mb-2" />
            <p className="font-medium text-slate-900">Validation</p>
            <p className="text-sm text-slate-500">Find invalid emails</p>
          </div>
        </div>

        {!result ? (
          /* Upload Section */
          <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
            {/* Dropzone */}
            <div
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer
                ${file ? 'border-blue-400 bg-blue-50' : 'border-slate-300 hover:border-blue-400 hover:bg-slate-50'}`}
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => fileInputRef.current?.click()}
              data-testid="email-formatter-dropzone"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => handleFileSelect(e.target.files[0])}
                className="hidden"
                data-testid="email-formatter-file-input"
              />
              
              {file ? (
                <div className="space-y-3">
                  <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mx-auto">
                    <FileSpreadsheet className="w-7 h-7 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{file.name}</p>
                    <p className="text-sm text-slate-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); resetTool(); }}
                  >
                    Change file
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mx-auto">
                    <Upload className="w-7 h-7 text-slate-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Drop your CRM export here</p>
                    <p className="text-sm text-slate-500">CSV or Excel file (max 50MB)</p>
                  </div>
                </div>
              )}
            </div>

            {/* Process Button */}
            {file && (
              <Button
                onClick={processFile}
                disabled={processing}
                className="w-full mt-6 h-14 text-lg bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="email-formatter-process-btn"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Fixing Emails...
                  </>
                ) : (
                  <>
                    <Mail className="w-5 h-5 mr-2" />
                    Fix Email Addresses (Free)
                  </>
                )}
              </Button>
            )}

            {/* How it works */}
            <div className="mt-8 pt-6 border-t border-slate-100">
              <h3 className="font-semibold text-slate-900 mb-4">What this tool fixes:</h3>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Common typos:</strong> gmal.com → gmail.com, hotmal.com → hotmail.com</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Formatting:</strong> Removes extra spaces, converts to lowercase</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Validation:</strong> Detects invalid formats, multiple @ symbols</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Trailing dots:</strong> Removes trailing dots from domains</span>
                </li>
              </ul>
            </div>
          </div>
        ) : (
          /* Results Section */
          <div className="space-y-6">
            {/* Stats Card */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Mail className="w-7 h-7 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">Emails Processed!</h2>
                  <p className="text-slate-500">{result.file_name}</p>
                </div>
              </div>

              <div className="grid sm:grid-cols-4 gap-4 mb-6">
                <div className="bg-slate-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-slate-900">{result.total_rows}</p>
                  <p className="text-sm text-slate-500">Total Rows</p>
                </div>
                <div className="bg-emerald-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-emerald-600">{result.valid_count}</p>
                  <p className="text-sm text-slate-500">Valid</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-blue-600">{result.fixed_count}</p>
                  <p className="text-sm text-slate-500">Fixed</p>
                </div>
                <div className="bg-red-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-red-600">{result.invalid_count}</p>
                  <p className="text-sm text-slate-500">Invalid</p>
                </div>
              </div>

              {/* Preview Table */}
              <div className="border border-slate-200 rounded-xl overflow-hidden mb-6">
                <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                  <p className="font-medium text-slate-700">Preview (first 5 rows)</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Row</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Status</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Original Email</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Fixed Email</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Issues</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {result.preview?.map((row, i) => (
                        <tr key={i} className="hover:bg-slate-50">
                          <td className="px-4 py-3 text-slate-500">#{row.row}</td>
                          <td className="px-4 py-3">{getStatusIcon(row.status)}</td>
                          <td className="px-4 py-3 font-mono text-slate-600">{row.original || '-'}</td>
                          <td className="px-4 py-3 font-mono text-blue-600 font-medium">{row.fixed || '-'}</td>
                          <td className="px-4 py-3 text-slate-500 text-xs">{row.issues || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Download Button - With Email Capture for Anonymous Users */}
              <Button 
                onClick={() => {
                  if (user?.email) {
                    window.open(`${API_URL}${result.download_url}`, '_blank');
                    toast.success('Download started!');
                  } else {
                    setShowEmailModal(true);
                  }
                }}
                className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Download className="w-5 h-5 mr-2" />
                Download Fixed File
              </Button>
            </div>

            {/* Freemium Upsell */}
            {result.total_rows > 100 && (
              <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-2xl p-6 sm:p-8 text-white">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold mb-2">
                      We fixed the first 100 rows for free!
                    </h3>
                    <p className="text-indigo-100 mb-4">
                      To fix the remaining <span className="font-bold text-white">{result.total_rows - 100} rows</span> and 
                      access 50+ other cleaning rules, sign up for free.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Link to="/login">
                        <Button className="bg-white text-indigo-600 hover:bg-indigo-50 w-full sm:w-auto">
                          Sign Up Free
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* All rows processed */}
            {result.total_rows <= 100 && (
              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 text-center">
                <CheckCircle className="w-12 h-12 text-blue-500 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-blue-800 mb-2">
                  All {result.total_rows} emails processed!
                </h3>
                <p className="text-blue-700 mb-4">
                  Need more cleaning? Try our full data cleaner with 50+ rules.
                </p>
                <Link to="/login">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                    Try Full Data Cleaner (Free)
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            )}

            {/* Process Another */}
            <div className="text-center">
              <Button variant="ghost" onClick={resetTool}>
                Process Another File
              </Button>
            </div>
          </div>
        )}

        {/* Trust Badges */}
        {!result && (
          <div className="mt-8 flex items-center justify-center gap-6 text-sm text-slate-500">
            <div className="flex items-center gap-1">
              <Shield className="w-4 h-4" />
              <span>Secure processing</span>
            </div>
            <div className="flex items-center gap-1">
              <CheckCircle className="w-4 h-4" />
              <span>No signup required</span>
            </div>
          </div>
        )}
      </main>
      
      {/* Email Capture Modal for Anonymous Users */}
      <EmailCaptureModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onSuccess={(downloadUrl) => {
          window.open(`${API_URL}${result.download_url}`, '_blank');
          toast.success('Download started!');
          setShowEmailModal(false);
        }}
        tool="email-formatter"
        fileId={result?.file_id || result?.download_url?.split('/').pop()}
        toolName="Email Fixer"
        rowsProcessed={result?.total_rows}
        accentColor="blue"
      />
      
      <SupportButton />
    </div>
  );
};

export default EmailFormatterPage;
